import { useCallback, useEffect, useRef, useState } from 'react'
import type { Socket } from 'socket.io-client'
import type { MeetingInviteEvent } from '@ai-meeting/shared'
import {
  INVITE_SYNC_TIMEOUT_MS,
  MEETING_INVITED_EVENT,
  MEETING_INVITES_SYNC,
  createMeetingInviteSocket,
} from '@/services/meeting-invites'
import { useStore } from '@/store/useStore'

/**
 * How many invites are kept on screen at once. Someone who shares a meeting
 * with a whole team produces one invite per recipient, not per viewer, so this
 * only fills up when several different people invite the same person at once —
 * at which point the oldest is the least useful.
 */
const MAX_VISIBLE_INVITES = 3

/**
 * How often the ongoing list is re-asked while the tab is being looked at.
 *
 * Nothing is ever pushed to say a room has ended — the sharer's machine is the only one that knows,
 * and an abandoned meeting announces nothing — so without this a card sits there until the socket
 * happens to reconnect. The same answer also carries rooms this tab missed the push for, since an
 * invite is a live nudge that is not queued for a socket which was not listening.
 *
 * Twenty seconds because the cost of being wrong is asymmetric and small either way: a card that
 * lingers a few seconds is a wasted click at worst, and the request is one socket message with no
 * body.
 */
const ONGOING_REFRESH_MS = 20_000

/** Whether a frame names a room that can actually be joined. */
function isJoinableInvite(event: MeetingInviteEvent | undefined): boolean {
  // No room means this was an ordinary History share (BRAI-417), which has nothing to join — the
  // meeting simply appears in the recipient's list, exactly as it did before invites existed.
  return Boolean(
    event?.meetingId &&
      event.sharedRoomId &&
      typeof event.sourceLang === 'string' &&
      typeof event.targetLang === 'string',
  )
}

export interface UseMeetingInvitesReturn {
  /** Newest first. Empty until an invite arrives; never restored across reloads. */
  invites: MeetingInviteEvent[]
  /**
   * Every room this user was invited to that is still running, newest first.
   *
   * A different list from {@link invites} on purpose, not a copy of it. That one is a stack of
   * notifications — capped, and dismissible because a nudge is something you are finished with.
   * This one is the state of the world, and turning off a nudge does not end somebody else's
   * meeting, so nothing here is dismissed and nothing is capped.
   *
   * Rebuilt from scratch on every connection. The server replays the live rooms each time a socket
   * connects, and nothing is ever pushed to retire one — so starting empty is what lets a reconnect
   * drop the rooms that ended while this tab was away.
   */
  ongoing: MeetingInviteEvent[]
  dismiss: (meetingId: string) => void
  /**
   * Whether that room is still joinable, asked of the server right now.
   *
   * Answers `true` when it cannot tell — offline, or the socket did not
   * answer in time. Failing closed there would block a legitimate join over a
   * blip, and a browser that cannot reach the server cannot create the
   * meeting either, so the error surfaces a moment later on the real call.
   */
  confirmStillLive: (meetingId: string) => Promise<boolean>
}

/**
 * Live "you were invited to a meeting" pushes for the signed-in user (BRAI-533).
 *
 * Nothing is persisted here, and nothing needs to be: the server replays every
 * room that is still running when the socket connects, so a reload, a late
 * sign-in and a reconnect all arrive back at the same list without this hook
 * keeping its own copy. Browser storage would only add a second, staler
 * answer — one that could offer a room that ended while the tab was closed.
 *
 * A consequence worth knowing: a dismissal is in memory too, so an invite the
 * user dismissed reappears after a reload while its room is still running.
 * That is the honest answer — they are still invited and it is still live —
 * but it is a decision, not an oversight.
 */
export function useMeetingInvites(): UseMeetingInvitesReturn {
  const isAuthenticated = useStore((s) => s.isAuthenticated)
  const [invites, setInvites] = useState<MeetingInviteEvent[]>([])
  const [ongoing, setOngoing] = useState<MeetingInviteEvent[]>([])
  const dismissedRef = useRef<Set<string>>(new Set())

  const socketRef = useRef<Socket | null>(null)

  const dismiss = useCallback((meetingId: string) => {
    dismissedRef.current.add(meetingId)
    setInvites((current) => current.filter((i) => i.meetingId !== meetingId))
  }, [])

  /**
   * The rooms the server says are joinable as of now, or null when it could not be asked.
   *
   * Null and an empty array are different answers, and both callers depend on the difference: one
   * means "nothing is running", the other means "ask again later". Collapsing them would empty the
   * screen over a dropped packet.
   */
  const requestSync = useCallback(async (): Promise<MeetingInviteEvent[] | null> => {
    const socket = socketRef.current
    if (!socket?.connected) return null

    const answer = await new Promise<{
      ok?: boolean
      invites?: MeetingInviteEvent[]
    } | null>((resolve) => {
      const timer = window.setTimeout(() => resolve(null), INVITE_SYNC_TIMEOUT_MS)
      socket.emit(MEETING_INVITES_SYNC, {}, (reply: unknown) => {
        window.clearTimeout(timer)
        resolve(reply as { ok?: boolean; invites?: MeetingInviteEvent[] })
      })
    })

    if (!answer?.ok || !Array.isArray(answer.invites)) return null
    return answer.invites.filter(isJoinableInvite)
  }, [])

  const confirmStillLive = useCallback(
    async (meetingId: string) => {
      const live = await requestSync()
      // A server that could not answer is not a server saying "it ended".
      if (!live) return true

      // Only answers. Dropping the row here would race the caller, which wants to
      // say "that meeting has ended" in the place the row was.
      return live.some((i) => i.meetingId === meetingId)
    },
    [requestSync],
  )

  useEffect(() => {
    if (!isAuthenticated) {
      // Signing out drops the queue: the next person at this browser must not
      // inherit invites addressed to the previous one.
      dismissedRef.current.clear()
      setInvites([])
      setOngoing([])
      return undefined
    }

    const socket = createMeetingInviteSocket()
    socketRef.current = socket

    function onInvited(event: MeetingInviteEvent) {
      if (!isJoinableInvite(event)) return
      setInvites((current) => {
        // A re-share pushes the same meeting again. Replacing rather than
        // appending keeps one row per meeting, and re-showing a dismissed one
        // is intended: being invited a second time is a new nudge.
        dismissedRef.current.delete(event.meetingId)
        const others = current.filter((i) => i.meetingId !== event.meetingId)
        return [event, ...others].slice(0, MAX_VISIBLE_INVITES)
      })
      // Replaced rather than appended for the same reason, and kept whole: the cap belongs to the
      // notification stack, not to a list of what is actually running.
      setOngoing((current) => [
        event,
        ...current.filter((i) => i.meetingId !== event.meetingId),
      ])
    }

    // Emptied on every connect, including a reconnect, because the replay that follows is the
    // server's current answer and this list has no other way of losing a room that has ended.
    function onConnect() {
      setOngoing([])
    }

    /**
     * Brings both lists up to date with the server's current answer.
     *
     * The ongoing list is replaced, because it is meant to be exactly what is running.
     *
     * The notification stack is only ever *filtered*, never rewritten. Filtering drops a nudge
     * whose meeting has since ended — one that would otherwise sit there offering a Join that
     * cannot work — while adding nothing back, which is the distinction that matters: this list is
     * a record of which nudges arrived and which the reader has dealt with, and rewriting it would
     * resurrect every dismissed invite once every twenty seconds.
     */
    async function refreshOngoing() {
      if (document.visibilityState === 'hidden') return
      const live = await requestSync()
      if (!live) return

      setOngoing(live)
      setInvites((current) =>
        current.filter((invite) => live.some((room) => room.meetingId === invite.meetingId)),
      )
    }

    // Asked again the moment the tab is looked at, not only on the next tick: coming back to a tab
    // is exactly when its contents are most likely to have gone stale, and waiting out the interval
    // there would show a list that is known to be out of date.
    function onVisible() {
      if (document.visibilityState === 'visible') void refreshOngoing()
    }

    const refreshTimer = window.setInterval(() => void refreshOngoing(), ONGOING_REFRESH_MS)
    document.addEventListener('visibilitychange', onVisible)

    socket.on(MEETING_INVITED_EVENT, onInvited)
    socket.on('connect', onConnect)
    socket.connect()

    return () => {
      window.clearInterval(refreshTimer)
      document.removeEventListener('visibilitychange', onVisible)
      socket.off(MEETING_INVITED_EVENT, onInvited)
      socket.off('connect', onConnect)
      socket.disconnect()
      socketRef.current = null
    }
  }, [isAuthenticated, requestSync])

  return { invites, ongoing, dismiss, confirmStillLive }
}
